import React, { useState, useEffect, useCallback } from 'react';
import { Button, Spinner, Modal } from 'react-bootstrap';
import appUrl from '../../appUrl';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import filterFactory, { textFilter } from 'react-bootstrap-table2-filter';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Table.css';
import ImageCarousel from '../Carousel';
import { useParams } from 'react-router-dom';
import { FaStar } from "react-icons/fa";
import { FaTrashCan } from "react-icons/fa6";

const ReviewsTable = () => {
    const { name, businessId } = useParams();
    const AppUrl = appUrl();
    const [dataLoading, setDataLoading] = useState(false);
    const [projects, setProjects] = useState([]);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [showModal, setShowModal] = useState({ show: false, id: null });
    const [modalContent, setModalContent] = useState({ type: "", content: ""});
    const [refreshData, setRefreshData] = useState(false);

    const fetchAllReviews = useCallback(async () => {
        setDataLoading(true);
        const token = localStorage.getItem("token");
        try {
            const response = await fetch(businessId ? `${AppUrl}/reviews/all?businessId=${businessId}` : `${AppUrl}/reviews/all`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            const data = await response.json();
            if (data.status) {
                setProjects(data.reviews);
                setTotalCount(data.reviews.length);
            } else {
                toast.error("Failed to fetch data");
            }
        } catch (error) {
            console.error("Error fetching reports:", error);
            toast.error("Error fetching reports");
        } finally {
            setDataLoading(false);
        }
    }, [AppUrl, businessId]);

    const handlePageChange = (page, sizePerPage) => {
        setCurrentPage(page);
        setPageSize(sizePerPage);
    };

    useEffect(() => {
        fetchAllReviews();
    }, [fetchAllReviews, refreshData]);

    const handleShowImages = (contentType, images) => {
        if(contentType === 'review_images' && Array.isArray(images)){
            setShowModal({show: true })
            setModalContent({ type: contentType, content: images });
        }
    }

    const handleDelete = async (e, type, businessId) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        if(!token) throw new Error("Invalid Operation!")

        try {
            setDataLoading(true);
            const response = await fetch(`${AppUrl}/reviews/delete?businessId=${businessId}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            const data = await response.json();
            if(data.status){
                toast.success('Review deleted successfully!');
                setRefreshData(prev => !prev)
            } else{
                throw new Error("Failed to delete review")
            }
        } catch (error) {
            console.error(error);
            toast.error(error)
        } finally {
            setDataLoading(false);
            setShowModal({ show: false });
        }
    }

    const columns = [
        {
            text: 'No.',
            dataField: 'index',
            formatter: (cell, row, rowIndex) => pageSize * (currentPage - 1) + rowIndex + 1
        },
        {
            text: 'Customer',
            dataField: 'userId.username',
            formatter: (cell) => {
                return cell ? cell.charAt(0).toUpperCase() + cell.slice(1) : '';
            },
            filter: textFilter({ placeholder: '' })
        },
        {
            text: 'Business',
            dataField: 'businessId.business_name',
            filter: textFilter({ placeholder: '' })
        },
        {
            text: 'Review',
            dataField: 'description',
            filter: textFilter({ placeholder: '' })
        },
        {
            text: 'Rating',
            dataField: 'rating',
            filter: textFilter({ placeholder: '' }),
            formatter: (cell) => {
                if (!cell) return '';
            
                return (
                    <div className='flex flex-col'>
                        <span>{cell}</span>
                        <div className='flex-center'>
                            {[...Array(cell)].map((_, index) => (
                                <FaStar key={index} color="gold" />
                            ))}
                        </div>
                    </div>
                );
            }
        },
        {
            text: 'DateTime',
            dataField: 'updatedAt',
            filter: textFilter({ placeholder: '' }),
            formatter: (cell) => {
                if (!cell) return '-';
                return new Date(cell).toLocaleString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: '2-digit', 
                    hour: '2-digit', 
                    minute: '2-digit', 
                    hour12: true 
                });
            }
        },
        {
            text: 'Images',
            dataField: 'images',
            // filter: textFilter({ placeholder: '' }),
            formatter: (cell, row) => row.images.length > 0 ? 
                        <div className='px-2'>
                            <Button size='sm' className='view-button'
                                onClick={() => handleShowImages('review_images', row.images)}>
                                View Images
                            </Button> 
                        </div> : <p className='text-muted'>No Images</p>
        },
        {
            text: 'Actions',
            formatter: (cell, row) => (
                <div className='flex-center'>
                    <span onClick={() => handleModal("delete_review", row)} className='user-crud-icon' style={{ color: 'red' }}>
                        <FaTrashCan/>
                    </span>
                </div>
            )
        }
    ];

    const handleModal = (behaviour, row) => {
        setModalContent({ type: behaviour, content: row });
        setShowModal({ show: true })
    };

    return (
        <>
            <ToastContainer />
            {dataLoading ? (
                <div className='loading-spinner'>
                    <Spinner animation="border" role="status" />
                </div>
            ) : (
                <>
                    {name && <div className='text-lg'>
                        <span>
                            Reviews for Business:
                        </span>
                        <span className='font-bold mx-1'>
                            {name}
                        </span>
                    </div>}
                    <BootstrapTable
                        bootstrap4
                        keyField="_id"
                        data={projects}
                        columns={columns}
                        filter={filterFactory()}
                        pagination={paginationFactory({
                            page: currentPage,
                            sizePerPage: pageSize,
                            totalSize: totalCount,
                            onPageChange: handlePageChange,
                            sizePerPageRenderer: () => null,
                            showTotal: true,
                            paginationTotalRenderer: (from, to, total) => (
                                <div className="pagination-and-entries">
                                    <p className="entries">
                                        Showing {from} to {to} of {total} entries
                                    </p>
                                </div>
                            )
                        })}
                    />
                </>
            )}

            <Modal
                show={showModal.show}
                onHide={() => setShowModal({ show: false, id: null })}>
                <Modal.Header closeButton>
                    {modalContent.type === 'review_images' ? <Modal.Title>Review Images</Modal.Title> :
                    <Modal.Title>Delete Review</Modal.Title>}
                </Modal.Header>
                <Modal.Body>
                {modalContent.type === 'review_images' ? <ImageCarousel data={modalContent.content}/> :
                    <div>
                        Are you sure, you want to delete this review ? 
                    </div>
                }
                </Modal.Body>
                {modalContent.type === "delete_review" && <Modal.Footer>
                    <Button className='modal-cancel-btn' variant="secondary" onClick={() => setShowModal({ show: false, id: null })}>
                        Cancel
                    </Button>
                    <Button
                        disabled={dataLoading}
                        className='modal-delete-btn'
                        variant="danger" 
                        onClick={(e) => handleDelete(e, modalContent.type, modalContent.content._id)}>
                        {dataLoading ? 
                        <div className='flex-center'>
                            <Spinner style={{ width: '25px', height: '25px'}} animation="border" role="status" />
                        </div>
                        : 
                        <span>Delete</span>}
                    </Button>
                </Modal.Footer>}
            </Modal>
        </>
    );
};

export default ReviewsTable;
